import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  NotebookPen, Plus, Pencil, Trash2, FileText, FileSpreadsheet,
  Loader2, MapPin, Clock, Settings2, Search, X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';
import { SendReportButton } from '../components/common/SendReportButton';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });
const user = JSON.parse(localStorage.getItem('user') || '{}');
const canManageAccess = ['super_admin', 'admin', 'hr_manager'].includes(user.role);

const fmtDate = (d) => {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};
const timeRange = (e) => (e.start_time && e.end_time) ? `${e.start_time} – ${e.end_time}` : (e.start_time || e.end_time || '—');

const blankEntry = { employee_id: '', log_date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', location: '', activity: '' };

const Logbook = () => {
  const [employees, setEmployees] = useState([]);
  const [empFilter, setEmpFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const [entryOpen, setEntryOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [entry, setEntry] = useState(blankEntry);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const [accessOpen, setAccessOpen] = useState(false);
  const [accessSearch, setAccessSearch] = useState('');

  const loadEmployees = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/logbook/employees`, { headers: authHeaders() });
      setEmployees(r.data || []);
    } catch { toast({ title: 'Failed to load employees', variant: 'destructive' }); }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (empFilter) params.append('employee_id', empFilter);
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      const r = await axios.get(`${API}/api/logbook/entries?${params.toString()}`, { headers: authHeaders() });
      setEntries(r.data || []);
    } catch { toast({ title: 'Failed to load entries', variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [empFilter, start, end]);

  useEffect(() => { loadEmployees(); }, [loadEmployees]);
  useEffect(() => { loadEntries(); }, [loadEntries]);

  const enabledEmployees = employees.filter((e) => e.logbook_enabled);

  // ── Access toggle ──
  const toggleAccess = async (emp, enabled) => {
    setEmployees((prev) => prev.map((e) => e.id === emp.id ? { ...e, logbook_enabled: enabled } : e));
    try {
      await axios.put(`${API}/api/logbook/employees/${emp.id}/access`, { enabled }, { headers: authHeaders() });
      toast({ title: enabled ? `Logbook enabled for ${emp.name}` : `Logbook disabled for ${emp.name}` });
    } catch (e) {
      setEmployees((prev) => prev.map((x) => x.id === emp.id ? { ...x, logbook_enabled: !enabled } : x));
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  // ── Add / Edit entry ──
  const openAdd = () => { setEditing(null); setEntry({ ...blankEntry, employee_id: empFilter || '' }); setEntryOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setEntry({ employee_id: e.employee_id, log_date: e.log_date, start_time: e.start_time || '', end_time: e.end_time || '', location: e.location || '', activity: e.activity || '' });
    setEntryOpen(true);
  };
  const saveEntry = async () => {
    if (!entry.employee_id) { toast({ title: 'Select an employee', variant: 'destructive' }); return; }
    if (!entry.log_date) { toast({ title: 'Pick a date', variant: 'destructive' }); return; }
    if (!entry.location.trim()) { toast({ title: 'Enter a location', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      if (editing) await axios.put(`${API}/api/logbook/entries/${editing.id}`, entry, { headers: authHeaders() });
      else await axios.post(`${API}/api/logbook/entries`, entry, { headers: authHeaders() });
      toast({ title: editing ? 'Entry updated' : 'Entry added' });
      setEntryOpen(false);
      await Promise.all([loadEntries(), loadEmployees()]);
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };
  const doDelete = async () => {
    const e = delTarget; setDelTarget(null);
    try {
      await axios.delete(`${API}/api/logbook/entries/${e.id}`, { headers: authHeaders() });
      toast({ title: 'Entry deleted' });
      await Promise.all([loadEntries(), loadEmployees()]);
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  // ── Export ──
  const exportFile = async (format) => {
    if (!empFilter) { toast({ title: 'Pick an employee to export', variant: 'destructive' }); return; }
    try {
      const params = new URLSearchParams({ employee_id: empFilter, format });
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      const r = await axios.get(`${API}/api/logbook/export?${params.toString()}`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const win = window.open(url, '_blank');
      if (!win) {
        const a = document.createElement('a'); a.href = url;
        a.download = `Logbook.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast({ title: 'Export failed', variant: 'destructive' }); }
  };

  const exportQS = () => {
    const params = new URLSearchParams({ employee_id: empFilter, format: 'pdf' });
    if (start) params.append('start', start); if (end) params.append('end', end);
    return params.toString();
  };

  const accessList = employees.filter((e) =>
    !accessSearch || `${e.name} ${e.employee_code} ${e.department}`.toLowerCase().includes(accessSearch.toLowerCase()));
  const enabledCount = employees.filter((e) => e.logbook_enabled).length;

  return (
    <div className="space-y-6" data-testid="logbook-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <NotebookPen className="h-7 w-7 text-indigo-600" /> Master Logbook
          </h1>
          <p className="text-sm text-gray-500">Work-time timeline history for selected staff. Add or edit entries on their behalf.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManageAccess && (
            <Button variant="outline" onClick={() => setAccessOpen(true)} className="gap-1.5" data-testid="logbook-manage-access-btn">
              <Settings2 className="h-4 w-4" /> Manage Access <Badge variant="secondary" className="ml-1">{enabledCount}</Badge>
            </Button>
          )}
          <Button onClick={openAdd} className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="logbook-add-btn">
            <Plus className="h-4 w-4" /> Add Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-2">
              <Label className="text-xs">Employee</Label>
              <Select value={empFilter || 'all'} onValueChange={(v) => setEmpFilter(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="logbook-employee-filter"><SelectValue placeholder="All employees" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All employees</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.employee_code} · {e.name}{e.logbook_enabled ? '' : ' (logbook off)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="logbook-start" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="logbook-end" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="outline" size="sm" disabled={!empFilter} onClick={() => exportFile('pdf')} className="gap-1" data-testid="logbook-export-pdf">
              <FileText className="h-3.5 w-3.5" /> PDF
            </Button>
            <Button variant="outline" size="sm" disabled={!empFilter} onClick={() => exportFile('excel')} className="gap-1" data-testid="logbook-export-excel">
              <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
            </Button>
            {empFilter && (
              <SendReportButton
                reportUrl={`/api/logbook/export?${exportQS()}`}
                filename="Logbook.pdf"
                defaultSubject="Employee Logbook"
                defaultMessage="Please find attached the employee logbook for the selected period."
                size="sm"
                testid="logbook-email"
              />
            )}
            {(start || end || empFilter) && (
              <Button variant="ghost" size="sm" onClick={() => { setStart(''); setEnd(''); setEmpFilter(''); }} className="text-gray-500">Clear filters</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entries */}
      <Card>
        <CardHeader><CardTitle className="text-base">Entries ({entries.length})</CardTitle></CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-x-auto">
            {loading ? (
              <div className="py-10 text-center text-gray-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
            ) : entries.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm" data-testid="logbook-empty">No logbook entries for this filter.</div>
            ) : (
              <table className="w-full text-sm min-w-[820px]">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                  <tr>
                    {!empFilter && <th className="text-left p-2.5">Employee</th>}
                    <th className="text-left p-2.5">Date</th>
                    <th className="text-left p-2.5">Time</th>
                    <th className="text-left p-2.5">Location</th>
                    <th className="text-left p-2.5">Activity</th>
                    <th className="text-center p-2.5">By</th>
                    <th className="text-right p-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.id} className="border-t hover:bg-gray-50/50" data-testid={`logbook-row-${e.id}`}>
                      {!empFilter && <td className="p-2.5 text-gray-700">{e.employee_code} · {e.employee_name}</td>}
                      <td className="p-2.5 font-medium text-gray-900 whitespace-nowrap">{fmtDate(e.log_date)}</td>
                      <td className="p-2.5 text-gray-600 whitespace-nowrap"><Clock className="h-3 w-3 inline mr-1 text-gray-400" />{timeRange(e)}</td>
                      <td className="p-2.5 text-gray-700"><MapPin className="h-3 w-3 inline mr-1 text-indigo-400" />{e.location}</td>
                      <td className="p-2.5 text-gray-600">{e.activity || '—'}</td>
                      <td className="p-2.5 text-center">
                        <Badge variant="outline" className={e.source === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-sky-50 text-sky-700 border-sky-200'}>
                          {e.source === 'admin' ? 'HR' : 'Self'}
                        </Badge>
                      </td>
                      <td className="p-2.5 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(e)} data-testid={`logbook-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => setDelTarget(e)} data-testid={`logbook-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit entry dialog */}
      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="max-w-md" data-testid="logbook-entry-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Logbook Entry' : 'Add Logbook Entry'}</DialogTitle>
            <DialogDescription>Record where the employee was during work time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Employee *</Label>
              <Select value={entry.employee_id} onValueChange={(v) => setEntry((p) => ({ ...p, employee_id: v }))} disabled={!!editing}>
                <SelectTrigger data-testid="logbook-entry-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={entry.log_date} onChange={(e) => setEntry((p) => ({ ...p, log_date: e.target.value }))} data-testid="logbook-entry-date" />
              </div>
              <div>
                <Label className="text-xs">Start</Label>
                <Input type="time" value={entry.start_time} onChange={(e) => setEntry((p) => ({ ...p, start_time: e.target.value }))} data-testid="logbook-entry-start" />
              </div>
              <div>
                <Label className="text-xs">End</Label>
                <Input type="time" value={entry.end_time} onChange={(e) => setEntry((p) => ({ ...p, end_time: e.target.value }))} data-testid="logbook-entry-end" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Location *</Label>
              <Input value={entry.location} onChange={(e) => setEntry((p) => ({ ...p, location: e.target.value }))} placeholder="e.g. Yas Mall / Warehouse, Mussafah" data-testid="logbook-entry-location" />
            </div>
            <div>
              <Label className="text-xs">Activity</Label>
              <Textarea value={entry.activity} onChange={(e) => setEntry((p) => ({ ...p, activity: e.target.value }))} rows={2} placeholder="What were they doing?" data-testid="logbook-entry-activity" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEntryOpen(false)}>Cancel</Button>
            <Button onClick={saveEntry} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="logbook-entry-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage access dialog */}
      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="max-w-lg" data-testid="logbook-access-dialog">
          <DialogHeader>
            <DialogTitle>Logbook Access</DialogTitle>
            <DialogDescription>Choose which employees can use the Logbook in their portal. ({enabledCount} enabled)</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            <Input value={accessSearch} onChange={(e) => setAccessSearch(e.target.value)} placeholder="Search name / code / department" className="pl-9" data-testid="logbook-access-search" />
          </div>
          <div className="border rounded-lg max-h-[50vh] overflow-y-auto divide-y">
            {accessList.map((e) => (
              <div key={e.id} className="flex items-center justify-between p-3" data-testid={`logbook-access-row-${e.employee_code}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{e.name}</p>
                  <p className="text-[11px] text-gray-500">{e.employee_code} · {e.department || '—'} · {e.entry_count} entries</p>
                </div>
                <Switch checked={!!e.logbook_enabled} onCheckedChange={(v) => toggleAccess(e, v)} data-testid={`logbook-access-toggle-${e.employee_code}`} />
              </div>
            ))}
            {accessList.length === 0 && <div className="p-6 text-center text-sm text-gray-400">No matching employees.</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <AlertDialogContent data-testid="logbook-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entry?</AlertDialogTitle>
            <AlertDialogDescription>Delete the {delTarget && fmtDate(delTarget.log_date)} entry ({delTarget?.location})? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Logbook;
